import { Court } from './../../../_shared/models/csTypes/court';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  computed,
  inject,
  input,
} from '@angular/core';
import { IonicModule } from '@ionic/angular';
import { Venue } from '../../../_shared/models/csTypes/venue';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ToastService } from '../../../_core/services/toast.service';
import { VenueDetailsService } from '../venue-details.service';
import { Subject, takeUntil } from 'rxjs';
import { Camera } from '../../../_shared/models/camera.model';
import { VenueVideoBoardsService } from '../venue-video-boards/venue-video-boards.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PartnerNamePipe } from '../../../_shared/pipes/partner-name.pipe';

@Component({
  selector: 'app-venue-courts',
  templateUrl: './venue-courts.component.html',
  styleUrls: ['./venue-courts.component.scss'],
  standalone: true,
  imports: [IonicModule, CommonModule, ReactiveFormsModule, TranslateModule, PartnerNamePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VenueCourtsComponent implements OnDestroy {
  private toastService = inject(ToastService);
  private venueDetailsService = inject(VenueDetailsService);
  private venueVideoBoardService = inject(VenueVideoBoardsService);
  private translateService = inject(TranslateService);

  videoBoards = computed(() => this.venueVideoBoardService.videoBoards());

  venue = this.venueDetailsService.venue;
  selectedCourt: Court | null = null;

  selectedCourts: string[] = [];
  isCourtModalOpen = false;

  isIntegrationModalOpen = false;
  isAddIntegrationCardOpen = false;
  selectedCourtForIntegrationModal: Court;

  isVideoBoardModalOpen = false;

  courtForm = new FormGroup({
    courtName: new FormControl('Court', Validators.required),
    count: new FormControl(1, [Validators.required, Validators.min(1)]),
    startAt: new FormControl(this.venue()?.courts?.length + 1 || 1),
    surfaceType: new FormControl('HARD'),
  });

  courtUpdateForm = new FormGroup({
    courtName: new FormControl('Court', Validators.required),
  });
  courtsListForm = new FormGroup({
    courts: new FormControl(),
  });

  private readonly destroy$: Subject<void> = new Subject();

  constructor() {
    this.venueDetailsService.venueWithUpdatedCourts$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.isCourtModalOpen = false;
      });

    this.venueDetailsService.venueWithDeletedCourts$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.selectedCourts = [];
      });

    this.venueDetailsService.venueWithEditedCourts$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.selectedCourts = [];
        this.selectedCourt = null;
      });

    this.venueDetailsService.venueWithAddedDevice$
      .pipe(takeUntil(this.destroy$))
      .subscribe((court) => (this.selectedCourtForIntegrationModal = court));

    this.venueDetailsService.venueWithRemovedDevice$
      .pipe(takeUntil(this.destroy$))
      .subscribe((court) => (this.selectedCourtForIntegrationModal = court));

    this.venueDetailsService.venueWithAddedVideoBoard$
      .pipe(takeUntil(this.destroy$))
      .subscribe((court) => (this.selectedCourtForIntegrationModal = court));

    this.venueDetailsService.venueWithRemovedVideoBoard$
      .pipe(takeUntil(this.destroy$))
      .subscribe((court) => (this.selectedCourtForIntegrationModal = court));
  }

  async ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async createCourts() {
    if (this.courtForm.invalid) {
      await this.toastService.presentDangerToast(this.translateService.instant('All fields are required'));
      return;
    }

    this.courtForm.controls.startAt.setValue(
      this.venue()?.courts?.length + 1 || 1
    );
    this.venueDetailsService.createCourts$.next(this.courtForm.getRawValue());
  }

  async updateCourt() {
    if (this.selectedCourt) {
      this.venueDetailsService.updateCourt$.next({
        ...this.selectedCourt,
        courtName: this.courtUpdateForm.controls.courtName.value ?? undefined,
      });
      this.selectedCourt = null;
      this.courtUpdateForm.reset();
    }
    this.isCourtModalOpen = false;
  }

  openUpdateModel(_courtId: string) {
    this.selectedCourt =
      this.venue()?.courts?.find((court) => court._id === _courtId) || null;

    if (this.selectedCourt) {
      this.courtUpdateForm.controls.courtName.setValue(
        this.selectedCourt.courtName
      );
    } else {
      this.courtUpdateForm.reset();
    }
    this.courtsListForm.controls.courts.reset();
  }
  selectCourts(ev, _courtId: string) {
    if (ev.detail.checked) {
      this.selectedCourts.push(_courtId);
    } else {
      const index = this.selectedCourts.findIndex((sc) => sc === _courtId);
      this.selectedCourts.splice(index, 1);
    }
  }

  isCourtChecked(_courtId: string) {
    return this.selectedCourts.some((c) => c === _courtId);
  }

  async deleteCourts() {
    this.venueDetailsService.deleteCourts$.next(this.selectedCourts);

    this.selectedCourts = [];
    this.selectedCourt = null;
  }
  async onCourtActionChange(a: any) {}
  resetEditSelection() {
    this.selectedCourt = null;
    this.courtUpdateForm.reset();
  }

  openIntegrationModal(court: Court) {
    this.selectedCourtForIntegrationModal = court;
    this.isIntegrationModalOpen = true;
  }

  openVideoBoardModal(court: Court) {
    this.selectedCourtForIntegrationModal = court;
    this.isVideoBoardModalOpen = true;
  }

  showAddIntegration() {
    this.isAddIntegrationCardOpen = !this.isAddIntegrationCardOpen;
  }

  addDevice(camera: Camera) {
    this.venueDetailsService.addDeviceToCourt$.next({
      _courtId: this.selectedCourtForIntegrationModal._id,
      camera,
    });
  }

  removeDevice(_deviceId: string) {
    this.venueDetailsService.removeDeviceFromCourt$.next({
      _courtId: this.selectedCourtForIntegrationModal._id,
      _deviceId,
    });
  }

  addVideoBoard(_videoBoardId: string) {
    this.venueDetailsService.addVideoBoardToCourt$.next({
      _courtId: this.selectedCourtForIntegrationModal._id,
      _videoBoardId,
    });
  }

  removeVideoBoard(_videoBoardId: string) {
    this.venueDetailsService.removeVideoBoardFromCourt$.next({
      _courtId: this.selectedCourtForIntegrationModal._id,
      _videoBoardId,
    });
  }
}
